import { OverlayModule } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterOutlet } from '@angular/router';

import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsStoragePluginModule, SESSION_STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { NgxsModule, withNgxsNoopExecutionStrategy } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { NgxImageCompressService } from 'ngx-image-compress';
import { OnlineStatusModule } from 'ngx-online-status';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { ToastNoAnimationModule } from 'ngx-toastr';

import { LoadingState } from '@shared/store/loading/loading.state';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { ErrorMessageInterceptor } from './auth/interceptors/error-message.interceptor';
import { cacheInterceptor, dedupeInterceptor } from './core-v2/routing/interseptor/dedupe.interceptor';
import { CustomErrorHandler } from './custom-error-handler';
import { CalendarState } from './dashboard/store/calendar/calendar.state';
import { ChooseDocumentsModule } from './popups/choose-documents/choose-documents.component';
import { ConnectionComponent } from './popups/connection/connection.component';
import { PopupFactoryService } from './popups/popup-factory.service';
import { UploadDocumentModule } from './popups/upload-document/upload-document.component';
import { ComponentsModule } from './shared/components-old/components.module';
import { PatientProfileInterceptor } from './shared/interseptors/patient-profile.interceptor';
import { SharedModule } from './shared/shared.module';
import { AppState } from './store/app.state';

@NgModule({
  declarations: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  exports: [],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    OverlayModule,
    RouterOutlet,
    AuthModule,
    AppRoutingModule,
    SharedModule,
    LoadingBarHttpClientModule,
    LoadingBarModule,
    ChooseDocumentsModule,
    UploadDocumentModule,
    NgxsFormPluginModule.forRoot(),
    NgxsModule.forRoot([AppState, CalendarState, LoadingState]),
    NgxsReduxDevtoolsPluginModule.forRoot({
      name: 'AAC',
      disabled: false,
    }),
    NgxsStoragePluginModule.forRoot({
      keys: [
        {
          key: 'filters',
          engine: SESSION_STORAGE_ENGINE,
        },
        // todo: move to tests module once we update nxgs to v18 (current one doesn't support storage module for lazy loaded states)
        {
          key: 'tests',
          engine: SESSION_STORAGE_ENGINE,
        },
        { key: 'patientExisting.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'patientReferral.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'patientNotes.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'patientAttachments.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'timesheets.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'timesheets.filtersDashboard', engine: SESSION_STORAGE_ENGINE },
        { key: 'redFlagsNew.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'incident.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'incidentDashboardNew.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'incidentDashboardNew.filtersNew', engine: SESSION_STORAGE_ENGINE },
        { key: 'incidentDashboardNew.filtersByPatient', engine: SESSION_STORAGE_ENGINE },
        { key: 'incidentDashboardNew.filtersAll', engine: SESSION_STORAGE_ENGINE },
        { key: 'incidentsAttachmentsAndNotes.filters', engine: SESSION_STORAGE_ENGINE },
        { key: 'admissionsV2.filtersAll', engine: SESSION_STORAGE_ENGINE },
        { key: 'admissionsV2.filtersByPatient', engine: SESSION_STORAGE_ENGINE },
        { key: 'admissionsV2.filtersByPeriod', engine: SESSION_STORAGE_ENGINE },
        { key: 'admissionsAttachmentsV2.filters', engine: SESSION_STORAGE_ENGINE },
      ],
    }),
    NgScrollbarModule,
    OverlayModule,
    ComponentsModule,
    OnlineStatusModule,
    ConnectionComponent,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    ToastNoAnimationModule.forRoot(),
  ],
  providers: [
    withNgxsNoopExecutionStrategy(),
    DatePipe,
    PopupFactoryService,
    NgxImageCompressService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorMessageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PatientProfileInterceptor,
      multi: true,
    },
    {
      provide: 'API_URL',
      useValue: environment.apiUrl,
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => (): void => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    ...(environment.production
      ? [
          {
            provide: ErrorHandler,
            useClass: CustomErrorHandler,
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
        ]
      : []),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([dedupeInterceptor, cacheInterceptor])),
  ],
})
export class AppModule {}
